import React from "react";
import { FontIcon, Spinner, SpinnerSize, Text, TooltipHost } from "@fluentui/react";
import { DragAndDropHelper } from "../../../../../../../utilities/DragAndDropHelper";
import { LanguageContext } from "../../../../../../../utilities/LocalizationModule";
import { dragZoneStyleSets, dropZoneStyleSets } from "../../../../../config/controlStyles";
import { mobileListItemStyle } from "../../../../../styles/mobileStyles";
import { treeViewItemStyle } from "../../../../helpers/treeHelperGeneral";
import { ComponentTypes, IDirectoryLocation } from "../../../../../config/config";
import { useDispatch, useSelector } from "react-redux";
import { setIsContentManagerSideMenuCollapsed } from "../../../../../redux/modules/contentManagerDirectory";
import { RootState } from "../../../redux/storeMain";
import { addUploadedFiles } from "../../../../../redux/modules/file";
import { logger } from "../../../../../../../utilities/logger";
import { IPageSettings } from "../../../../../interfaces/IPageSettings";
import { IOneDriveRootDetails } from "../../../../../interfaces/IOneDriveRootDetails";
import { getOneDriveRootDetails } from "../../../../../services/GraphService";
import { setOneDriveDetails, setWorkingDirectories } from "../../../../../redux/modules/workingDirectory";
import generalModule from "../../../../../../../utilities/GeneralModule";
import savingLocationStructureModule from "../../../../../../../utilities/SavingLocationStructureModule";
import { setSavingLocation } from "../../../../../redux/modules/savingLocation";
import { getLocalStorage, localStorageKeys } from "../../../../helpers/localStorageHelper";

interface IMainLocationItemProps {
    item: IDirectoryLocation;
    isMobile: boolean;
    isSideMenuCollapsed: boolean;
    onMainLocationClick: (item: IDirectoryLocation) => Promise<void>;
    onExpandCollapseClick: (item: IDirectoryLocation, isRefresh?: boolean) => Promise<void>;
    setContextAwareLoading: (isLoading: boolean, contentManagerOnly?: boolean) => void;
    onRefreshClick?: () => Promise<void>;
}

export const MainLocationItem = ({ item, isMobile, isSideMenuCollapsed, onMainLocationClick, onExpandCollapseClick, setContextAwareLoading, onRefreshClick }: IMainLocationItemProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const dispatch = useDispatch();

    const isSitesLoading = useSelector((state: RootState) => state.sharePointInfo.isSitesLoading);
    const isTeamsLoading = useSelector((state: RootState) => state.teams.isTeamsLoading);
    const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories?.workingDirectories?.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
    const oneDriveWebUrl = useSelector((state: RootState) => state.user.oneDriveWebUrl);
    const lastServerFetch_sites = useSelector((state: RootState) => state.sharePointInfo.lastServerFetch);
    const lastServerFetch_teams = useSelector((state: RootState) => state.teams.lastServerFetch);

    const [isDraggedOver, setIsDraggedOver] = React.useState(false);
    const dragCounter = React.useRef<number>(0);

    const isDropDisabled = React.useMemo((): boolean => {
        return item.type !== ComponentTypes.OneDrive || (DragAndDropHelper.sourceDirectory?.id && item.id === DragAndDropHelper.sourceDirectory.id);
    }, [item, DragAndDropHelper.sourceDirectory]);

    const dragEnter = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        if (DragAndDropHelper.validateDroppedFiles(event) || DragAndDropHelper.validateDroppedGridItems(event)) {
            event.preventDefault();
            event.stopPropagation();
            if (isDropDisabled) {
                event.dataTransfer.dropEffect = "none";
            }
            dragCounter.current = dragCounter.current + 1;
            setIsDraggedOver(true);
        }
    }, [isDropDisabled]);

    const dragOver = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (isDropDisabled) {
            event.dataTransfer.dropEffect = "none";
        }
    }, [isDropDisabled]);

    const dragLeave = React.useCallback((event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();
        if (isDraggedOver) {
            dragCounter.current = dragCounter.current - 1;
            if (!dragCounter.current || event.target === event.currentTarget) {
                setIsDraggedOver(false);
            }
        }
    }, [isDraggedOver]);

    const handleDrop = React.useCallback(async (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();

        dragCounter.current = 0;
        setIsDraggedOver(false);

        try {
            if (DragAndDropHelper.validateDroppedFiles(event)) {
                dispatch(addUploadedFiles(await DragAndDropHelper.handleDroppedFiles(event)));
                if (generalModule.isInConentMangerMode()) {
                    generalModule.setOpenPanelUploadPanel(true);
                    onMainLocationClick(item);
                }
                else {
                    const directories: IPageSettings[] = [{
                        type: item.type,
                        id: item.id,
                        directory: "",
                        displayName: item.name,
                        fileTitle: item.name,
                        webUrl: oneDriveWebUrl
                    }];
                    savingLocationStructureModule.setSavingLocationStructure(directories);
                    dispatch(setSavingLocation(
                        {
                            display: item.name,
                            webUrl: oneDriveWebUrl,
                            directory: "",
                        },
                        true
                    ));
                    // saveToOneDrive(item.name);
                    const savingDirectory = {
                        ...directories[0],
                        isSaveLocationStructureSet: true,
                        displayName: languageStrings.Properties,
                        type: ComponentTypes.Saving
                    }

                    const newLocations = [
                        ...directories,
                        savingDirectory,
                    ];

                    dispatch(setWorkingDirectories(newLocations));
                }
            }
            else if (generalModule.isInConentMangerMode() && DragAndDropHelper.validateDroppedGridItems(event, false, false, undefined, item)) {
                let oneDriveDetails: IOneDriveRootDetails;
                if (oneDriveRootDetails) {
                    oneDriveDetails = oneDriveRootDetails;
                }
                else {
                    setContextAwareLoading(true);
                    try {
                        oneDriveDetails = await getOneDriveRootDetails();
                        dispatch(setOneDriveDetails(oneDriveDetails));
                    } catch (err) {
                        logger(err, "Locations.tsx (onMainLocationClick)");
                    }
                    setContextAwareLoading(false);
                }

                const directories: IPageSettings[] = [{
                    type: item.type,
                    id: item.id,
                    directory: "",
                    displayName: item.name,
                    webUrl: oneDriveWebUrl,
                    sharePointListId: oneDriveDetails?.sharePointListId
                }];

                DragAndDropHelper.destinationDirectory = directories[0];
                DragAndDropHelper.handleGridMove(dispatch, oneDriveDetails.sharePointSiteUrl, directories, true, new URL(oneDriveWebUrl).pathname);
            }
        } catch (error) {
            logger(error, "MainLocationItem (handleDrop)");
        }
    }, [item, currentDirectory, oneDriveRootDetails, oneDriveWebUrl, onMainLocationClick]);

    const className = React.useMemo((): string => {
        let className: string;
        if (!isSideMenuCollapsed) {
            className = "list-item dropzone";
            if (isDraggedOver) {
                className += isDropDisabled
                    ? ` ${dragZoneStyleSets.dragzoneActive}`
                    : ` ${dropZoneStyleSets.dropzoneActive}`;
            }
        }
        return className;
    }, [isSideMenuCollapsed, isDraggedOver, isDropDisabled]);

    const showLoadingSpinner = React.useMemo((): boolean => {
        // No loading spinner when the side menu is collapsed or item has next href
        if (isSideMenuCollapsed || item.foldersNextHref) {
            return false;
        }

        // Show spinner if item is loading
        if (item.isLoading) {
            return true;
        }

        const hasCachedSites = getLocalStorage(localStorageKeys.sites);
        const hasCachedTeams = getLocalStorage(localStorageKeys.teams);

        // SharePoint: show spinner only if sites or teams are loading and not cached and wait for server response
        if (item.type === ComponentTypes.SharePoint) {
            return !hasCachedSites || !hasCachedTeams ? (isSitesLoading || isTeamsLoading || !lastServerFetch_sites || !lastServerFetch_teams) : (false);
        }

        // Teams: show spinner only if teams are loading and not cached and wait for server response
        if (item.type === ComponentTypes.Teams) {
            return !hasCachedTeams && isTeamsLoading && !lastServerFetch_teams;
        }

        return false;
    }, [item, isSideMenuCollapsed, isSitesLoading, isTeamsLoading, lastServerFetch_sites, lastServerFetch_teams]);

    const showChevron = React.useMemo((): boolean => {
        return !isSideMenuCollapsed
            && (item.type === ComponentTypes.Tess || (((!item.isLoading && item.canExpand) || (item.isLoading && item.foldersNextHref)) &&
                !(item.expanded && (!item.sites || item.sites?.length === 0) && (!item.accounts || item.accounts?.length === 0) && (!item.folders || item.folders?.length === 0))));
    }, [item, isSideMenuCollapsed]);

    React.useEffect(() => {
        let debounceTimer;
        if (isDraggedOver && showChevron && !item.expanded && !item.isLoading) {
            debounceTimer = setTimeout(() => {
                onExpandCollapseClick(item, false);
            }, 1500);
        }

        return () => {
            clearTimeout(debounceTimer);
        };
    }, [item, isDraggedOver, showChevron, onExpandCollapseClick]);

    return (
        !isSideMenuCollapsed
            ? <div
                className={className}
                style={isMobile ? mobileListItemStyle : { paddingLeft: 0 }}
                onClick={(e) => {
                    e.stopPropagation();
                    onMainLocationClick(item);
                }}
                onDragEnter={dragEnter}
                onDragOver={dragOver}
                onDragLeave={dragLeave}
                onDrop={item.type === ComponentTypes.OneDrive ? handleDrop : undefined}
            >
                <div className="list-item-link">
                    <div
                        style={treeViewItemStyle(item)}
                        onClick={(e) => {
                            if (showChevron && !showLoadingSpinner) {
                                e.stopPropagation();
                                onExpandCollapseClick(item, false);
                            }
                        }}
                    >
                        {showLoadingSpinner && <Spinner size={SpinnerSize.xSmall} />}
                        {showChevron && !showLoadingSpinner && <FontIcon iconName={item.expanded ? "ChevronDown" : "ChevronRight"} />}
                    </div>
                    <div className="list-item-icon" style={{ paddingLeft: !item.canExpand ? 10 : 0 }}>
                        <img src={item.imgUrl} height={20} width={20}></img>
                    </div>
                    <Text className="list-item-name">{item.name}</Text>
                </div>
                {item.expanded && <TooltipHost content={languageStrings.Refresh}>
                    <div className={isMobile ? "list-item-save-icon-mobile" : "list-item-save-icon"}>
                        <FontIcon
                            iconName="Refresh"
                            className="refresh-icon"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (onRefreshClick) {
                                    onRefreshClick();
                                }
                                else {
                                    onExpandCollapseClick(item, true);
                                }
                            }}
                        />
                    </div>
                </TooltipHost>}
            </div>
            : <div
                className="list-item-icon-sidemenu"
                onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setIsContentManagerSideMenuCollapsed(false));
                    onMainLocationClick(item);
                }}>
                <img src={item.imgUrl} height={16} width={16}></img>
            </div>
    )
};